import is from '@sindresorhus/is';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useRef } from 'react';
import Maybe from 'true-myth/maybe';
import type { StoreApi } from 'zustand';

import { createRealClock } from '~/shared/clock/real-clock';
import { ApplicationContext } from '~/shared/context/context';
import type { UserSettings } from '~/shared/data-contract/user-settings-schema';

import { createCompanyOnboardingEmitter } from './company-onboarding-emitter';
import type { CompanyOnboardingStore, OnboardingType } from './company-onboarding-store';
import { createCompanyOnboardingStore } from './company-onboarding-store';
import { createCreateNewJobArchitecture } from './create-new-job-architecture';
import { createFetchCompanyProfile } from './fetch-company-profile';
import { createFetchCvAnalysis } from './fetch-cv-analysis';
import { createGenerateCompanyDescription } from './generate-company-description';
import { createGenerateCompanyPriorities } from './generate-company-priorities';
import { createGenerateJobFamiliesWithAi } from './generate-job-families-with-ai';
import { createSendJobArchitectureMetadata } from './send-job-architecture-metadata';
import { createUpdateCompanyProfile } from './update-company-profile';
import { createUpdateUserSettings } from './update-user-settings';
import { createUploadCV } from './upload-cv';

const getOnboardingType = (userSettings: Maybe<UserSettings>): OnboardingType => {
    if (userSettings.get('startPage').equals(Maybe.just('company-onboarding'))) {
        return 'company';
    }

    return 'employee';
};

export const CompanyOnboardingContext = createContext<StoreApi<CompanyOnboardingStore> | null>(null);

export const CompanyOnboardingContextProvider: FunctionComponent<PropsWithChildren> = (properties) => {
    const storeRef = useRef<StoreApi<CompanyOnboardingStore>>(undefined);
    const {
        actions: { navigateTo, setLanguage },
        applicationState: { availableLanguages, language, userSettings },
        cobrainerApiFetcher,
        translate,
        validatingCobrainerApiFetcher,
    } = useContext(ApplicationContext);

    if (is.undefined(storeRef.current)) {
        const fetchCompanyProfile = createFetchCompanyProfile({ validatingCobrainerApiFetcher });
        const updateCompanyProfile = createUpdateCompanyProfile({ validatingCobrainerApiFetcher });
        const generateCompanyDescription = createGenerateCompanyDescription({ validatingCobrainerApiFetcher });
        const generateCompanyPriorities = createGenerateCompanyPriorities({ validatingCobrainerApiFetcher });
        const uploadCV = createUploadCV({ validatingCobrainerApiFetcher });
        const fetchCvAnalysis = createFetchCvAnalysis({ validatingCobrainerApiFetcher });
        const updateUserSettings = createUpdateUserSettings({ validatingCobrainerApiFetcher });
        const clock = createRealClock();
        const navigateToHome = (): void => {
            navigateTo({ pathname: '/home' }, { replace: true });
        };

        const companyOnboardingEmitter = createCompanyOnboardingEmitter({
            clock,
            createNewJobArchitecture: createCreateNewJobArchitecture({
                validatingCobrainerApiFetcher,
            }),
            fetchCompanyProfile,
            fetchCvAnalysis,
            generateCompanyDescription,
            generateCompanyPriorities,
            generateJobFamilies: createGenerateJobFamiliesWithAi({
                cobrainerApiFetcher,
            }),
            sendJobArchitectureMetadata: createSendJobArchitectureMetadata({
                cobrainerApiFetcher,
            }),
            updateCompanyProfile,
            updateUserSettings,
            uploadCV,
        });

        storeRef.current = createCompanyOnboardingStore({
            availableLanguages,
            companyOnboardingEmitter,
            initialApplicationLanguage: language.code,
            navigateToHome,
            onboardingType: getOnboardingType(userSettings),
            setApplicationLanguage: setLanguage,
            translate,
        });

        storeRef.current.getState().loadCompanyProfile();
    }

    return (
        <CompanyOnboardingContext.Provider value={storeRef.current}>
            {properties.children}
        </CompanyOnboardingContext.Provider>
    );
};
